import * as React from "react";
import AsyncAction from "../AsyncAction";
import sfetch from "../commons/utils/super-fetch";

interface ChooseNewPasswordProps {
  resetPasswordToken: string;
  onPasswordChanged: () => void;
}

interface ChooseNewPasswordState {
  password: FormField<string>;
  passwordConfirmation: FormField<string>;
  serverValidationError: boolean;
}

interface FormField<T> {
  value: T;
  pristine: boolean;
}

export default class ChooseNewPassword extends React.Component<
  ChooseNewPasswordProps,
  ChooseNewPasswordState
> {
  constructor(props: ChooseNewPasswordProps) {
    super(props);
    this.state = {
      password: {
        value: "",
        pristine: true,
      },
      passwordConfirmation: {
        value: "",
        pristine: true,
      },
      serverValidationError: false,
    };
  }

  private onSubmit() {
    if (
      this.state.password.value &&
      this.state.password.value === this.state.passwordConfirmation.value
    ) {
      return sfetch("/internal-api/v1/users/reset-password", {
        method: "POST",
        body: JSON.stringify({
          resetPasswordToken: this.props.resetPasswordToken,
          password: this.state.password.value,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
  }

  private onError(error) {
    this.setState({
      serverValidationError: error.status === 400,
    });
  }

  render() {
    const formIsInvalid = this.state.password.value !== this.state.passwordConfirmation.value;
    const passwordErrorMessage = this.state.serverValidationError ? (
      <small className="error-message" id="error-for-new-password1">
        {i18n["Page.ChooseNewPassword.PasswordError"]}
      </small>
    ) : null;

    const passwordConfirmationErrorMessage =
      formIsInvalid &&
      !this.state.password.pristine &&
      !this.state.passwordConfirmation.pristine ? (
        <small className="error-message" id="error-for-password-password2">
          {i18n["Page.ChooseNewPassword.PasswordMismatchError"]}
        </small>
      ) : null;

    return (
      <form className="react-form">
        <div className="title-bar">
          <h1 className="title">{i18n["Page.ChooseNewPassword.Title"]}</h1>
        </div>
        <div className={"input-group" + (passwordErrorMessage ? " invalid" : "")}>
          <label htmlFor="password">{i18n["Page.ChooseNewPassword.Password"]}</label>
          <input
            id="password"
            type="password"
            className={passwordErrorMessage ? "invalid" : null}
            placeholder={i18n["Page.ChooseNewPassword.PasswordPrompt"]}
            onChange={(e) => (
              e.persist(),
              this.setState((state) => ({
                password: {
                  value: e.target.value,
                  pristine: state.password.pristine,
                },
              }))
            )}
            onBlur={() =>
              this.setState((state) => ({
                password: {
                  value: state.password.value,
                  pristine: false,
                },
              }))
            }
            value={this.state.password.value}
            autoFocus
          />
          {passwordErrorMessage}
        </div>

        <div className={"input-group" + (passwordConfirmationErrorMessage ? " invalid" : "")}>
          <label htmlFor="password-confirmation">
            {i18n["Page.ChooseNewPassword.PasswordConfirmation"]}
          </label>
          <input
            id="password-confirmation"
            type="password"
            className={passwordConfirmationErrorMessage ? "invalid" : null}
            placeholder={i18n["Page.ChooseNewPassword.PasswordConfirmationPrompt"]}
            onChange={(e) => (
              e.persist(),
              this.setState((state) => ({
                passwordConfirmation: {
                  value: e.target.value,
                  pristine: state.passwordConfirmation.pristine,
                },
              }))
            )}
            onBlur={() =>
              this.setState((state) => ({
                passwordConfirmation: {
                  value: state.passwordConfirmation.value,
                  pristine: false,
                },
              }))
            }
            value={this.state.passwordConfirmation.value}
          />
          {passwordConfirmationErrorMessage}
        </div>
        <div className="action-container">
          <AsyncAction
            buttonLabel={i18n["Page.ChooseNewPassword.Submit"]}
            promise={() => this.onSubmit()}
            onError={(error) => this.onError(error)}
            onSuccess={() => this.props.onPasswordChanged()}
            disabled={
              !this.state.password.value || !this.state.passwordConfirmation.value || formIsInvalid
            }
          />
        </div>
      </form>
    );
  }
}
